import { BookOpen, GraduationCap, Users, Award, Calendar, Monitor, Clock } from 'lucide-react';
import type { Product } from './types';

export const educationProducts: Product[] = [
  {
    id: 'language-learning-translation-services',
    title: 'Language Learning & Translation Services',
    subtitle: 'Advanced language learning platform',
    description: 'Advanced language learning platform with personalised curricula, progress tracking, tutor management, assessment tools, and translation services for UK educational institutions.',
    icon: BookOpen,
    image: 'https://images.unsplash.com/photo-1571260899304-425eee4c7efc?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Education & Training',
    status: 'Available',
    features: [
      'AI-Powered Personalised Learning Curricula & Adaptive Pathways',
      'Advanced Progress Tracking & Multilingual Skill Assessment',
      'Intelligent Tutor Management & Dynamic Scheduling System',
      'Interactive Language Exercises & Gamified Learning Modules',
      'Speech Recognition & Advanced Pronunciation Training with AI Feedback',
      'Cultural Context Learning & Real-World Application Scenarios',
      'Professional Translation Services & Document Management Suite',
      'Multi-Language Support & Customizable Learning Paths',
      'Comprehensive Assessment Tools & International Certification Management',
      'Global Community Features & Language Exchange Networking',
      'Mobile Learning Platform & Advanced Offline Capabilities',
      'AI-Powered Learning Recommendations & Performance Analytics'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-green-500 to-blue-600',
    businessTypes: ['#education', '#language-learning', '#translation', '#training', '#institutions']
  },
  {
    id: 'complete-education-training-platform',
    title: 'Complete Education & Training Platform',
    subtitle: 'Comprehensive educational & learning solution',
    description: 'Integrated education and training platform combining learning management, student information systems, corporate training, course marketplace, and institutional administration for comprehensive educational delivery.',
    icon: GraduationCap,
    image: 'https://images.unsplash.com/photo-1571260899304-425eee4c7efc?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Education & Training',
    status: 'Available',
    businessTypes: ['#education', '#corporate-training', '#lms', '#e-learning', '#skills-development', '#school-management', '#student-information', '#institutions', '#administration'],
    features: [
      'Advanced Course Creation & Interactive Content Management Suite',
      'Intelligent Student Enrollment & Comprehensive Progress Tracking',
      'Comprehensive Student Records & Academic History Management',
      'Interactive Assessment Tools & Advanced Quiz Engine',
      'HD Video Conferencing & Virtual Classroom Integration',
      'Automated Assignment Submission & AI-Powered Grading',
      'Real-Time Attendance Tracking & Monitoring System',
      'Dynamic Discussion Forums & Real-Time Collaboration Tools',
      'Interactive Parent Portal & Multi-Channel Communication Tools',
      'Dynamic Timetable & Advanced Scheduling Management',
      'Integrated Fee Management & Secure Payment Processing',
      'Professional Certification & Digital Badge Management System'
    ],
    pricing: { setup: 48000, monthly: 2208 },
    color: 'from-blue-500 to-purple-600'
  },
  {
    id: 'virtual-classroom-platform',
    title: 'Virtual Classroom Platform',
    subtitle: 'Interactive online learning environment',
    description: 'Advanced virtual classroom solution with real-time collaboration, interactive whiteboards, breakout rooms, and comprehensive learning analytics.',
    icon: Monitor,
    image: 'https://images.unsplash.com/photo-1588072432836-e10032774350?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Education & Training',
    status: 'Available',
    businessTypes: ['#education', '#virtual-learning', '#online-classroom', '#remote-education', '#collaboration'],
    features: [
      'HD Video Conferencing & Screen Sharing',
      'Interactive Digital Whiteboards',
      'Breakout Rooms & Group Collaboration',
      'Real-Time Chat & Messaging',
      'Session Recording & Playback',
      'Attendance Tracking & Analytics',
      'Interactive Polls & Quizzes',
      'File Sharing & Document Collaboration',
      'Multi-Device Support & Mobile Access',
      'Integration with Learning Management Systems',
      'Automated Transcription & Closed Captions',
      'Virtual Hand Raising & Participation Tracking'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-purple-500 to-indigo-600'
  },
  {
    id: 'professional-certification-platform',
    title: 'Professional Certification Platform',
    subtitle: 'Complete certification management',
    description: 'Comprehensive certification platform with assessment creation, secure testing, digital badges, and compliance tracking for professional development.',
    icon: Award,
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Education & Training',
    status: 'Available',
    businessTypes: ['#certification', '#professional-development', '#assessment', '#compliance', '#training'],
    features: [
      'Assessment Creation & Management Tools',
      'Secure Online Testing Environment',
      'Digital Badge & Certificate Generation',
      'Certification Tracking & Renewal Management',
      'Proctoring & Anti-Cheating Features',
      'Competency-Based Assessment',
      'Automated Scoring & Results Processing',
      'Integration with Professional Bodies',
      'Continuing Education Credit Tracking',
      'Mobile Testing Capabilities',
      'Advanced Analytics & Reporting',
      'Multi-Language Assessment Support'
    ],
    pricing: { setup: 42000, monthly: 1932 },
    color: 'from-gold-500 to-yellow-600'
  },
  {
    id: 'skills-assessment-platform',
    title: 'Skills Assessment Platform',
    subtitle: 'Comprehensive competency evaluation',
    description: 'Advanced skills assessment platform with competency mapping, gap analysis, and personalized development planning.',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Education & Training',
    status: 'Available',
    features: [
      'Competency mapping',
      'Skills gap analysis',
      'Assessment creation tools',
      'Multi-format assessments',
      'Automated scoring',
      'Development planning',
      'Progress tracking',
      'Certification management',
      'Reporting & analytics',
      'Integration capabilities',
      'Mobile assessments',
      'Peer evaluations'
    ],
    pricing: { setup: 36000, monthly: 1656 },
    color: 'from-emerald-500 to-green-600',
    businessTypes: ['#skills-assessment', '#competency', '#evaluation', '#development', '#hr']
  },
  {
    id: 'microlearning-system',
    title: 'Microlearning System',
    subtitle: 'Bite-sized learning delivery',
    description: 'Microlearning platform with short-form content delivery, spaced repetition, and just-in-time learning capabilities.',
    icon: Clock,
    image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Education & Training',
    status: 'Available',
    features: [
      'Micro-content creation',
      'Spaced repetition algorithms',
      'Just-in-time delivery',
      'Mobile-first design',
      'Progress tracking',
      'Personalized pathways',
      'Gamification elements',
      'Offline access',
      'Integration APIs',
      'Performance analytics',
      'Social learning',
      'Multi-format support'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-blue-500 to-cyan-600',
    businessTypes: ['#microlearning', '#mobile-learning', '#just-in-time', '#personalized', '#bite-sized']
  },
  {
    id: 'training-content-library',
    title: 'Training Content Library',
    subtitle: 'Curated learning resource hub',
    description: 'Comprehensive training content library with curated courses, multimedia resources, and customizable learning paths.',
    icon: BookOpen,
    image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Education & Training',
    status: 'Available',
    features: [
      'Curated content library',
      'Multi-format resources',
      'Custom learning paths',
      'Content tagging',
      'Search & discovery',
      'Usage analytics',
      'Content recommendations',
      'Licensing management',
      'Quality assurance',
      'Mobile access',
      'Offline downloads',
      'Integration tools'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-indigo-500 to-purple-600',
    businessTypes: ['#content-library', '#curated-learning', '#resources', '#multimedia', '#paths']
  },
  {
    id: 'instructor-management-system',
    title: 'Instructor Management System',
    subtitle: 'Educator resource management',
    description: 'Complete instructor management platform with scheduling, qualifications tracking, performance evaluation, and resource allocation.',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Education & Training',
    status: 'Available',
    features: [
      'Instructor profiles',
      'Qualification tracking',
      'Scheduling management',
      'Performance evaluation',
      'Resource allocation',
      'Payment management',
      'Availability tracking',
      'Communication tools',
      'Reporting & analytics',
      'Mobile app access',
      'Integration capabilities',
      'Compliance tracking'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-orange-500 to-red-600',
    businessTypes: ['#instructor-management', '#scheduling', '#qualifications', '#evaluation', '#resources']
  },
  {
    id: 'learning-path-builder',
    title: 'Learning Path Builder',
    subtitle: 'Personalized curriculum design',
    description: 'Advanced learning path builder with AI-powered recommendations, adaptive sequencing, and personalized curriculum design.',
    icon: Calendar,
    image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Education & Training',
    status: 'Available',
    features: [
      'AI-powered recommendations',
      'Adaptive sequencing',
      'Prerequisite mapping',
      'Personalized curricula',
      'Progress tracking',
      'Competency alignment',
      'Learning analytics',
      'Path optimization',
      'Social learning',
      'Mobile support',
      'Integration tools',
      'Performance metrics'
    ],
    pricing: { setup: 55000, monthly: 2530 },
    color: 'from-purple-500 to-pink-600',
    businessTypes: ['#learning-paths', '#ai-recommendations', '#personalized', '#adaptive', '#curriculum']
  }
];
